import {
  type NewRoute,
  type TrackingDevice,
  type TrackingDriver,
  type TrackingIncident,
  type TrackingPath,
  type TrackingSpeedEvent,
  type TrackingStop,
  type TrackingSummary,
  type TrackingVehicle,
  type WebData,
  webDataError,
  webDataFetching,
  webDataNotAsked,
  webDataSuccess,
} from '@cobliteam/api-lib';
import type { DeviceVehicle } from '@cobliteam/api-lib/src/dashboard/device/definitions';

import type { Media, StartEndDateRange, TimestampInMillis, UUID } from '@cobliteam/common-types';
import type { TrackingAction } from './actions';
import { ActionTypes } from './actionTypes';

export interface TrackingState {
  driversWebData: WebData<TrackingDriver[]>;
  pathGeojsonWebData: WebData<TrackingPath>;
  roadSpeedEventsWebData: WebData<TrackingSpeedEvent[]>;
  vehicleSpeedEventsWebData: WebData<TrackingSpeedEvent[]>;
  stopWebData: WebData<TrackingStop[]>;
  summaryWebData: WebData<TrackingSummary | undefined>;
  incidentsWebData: WebData<TrackingIncident[]>;
  vehicleWebData: WebData<TrackingVehicle | undefined>;
  deviceWebData: WebData<TrackingDevice | undefined>;
  selectedTripTime: StartEndDateRange | null;
  savedRoutesWebData: WebData<NewRoute[]>;
  mediaEvents: WebData<Media[]>;
  selectedDate?: TimestampInMillis;
  selectedVehicleId?: UUID;
  selectedSavedRoute?: NewRoute;
  selectedTrigger?: any;
  selectedGeofenceId?: UUID;
  trackingTabOpen: boolean;
  devicesVehiclesWebData: WebData<DeviceVehicle[]>;
  selectedDeviceId?: UUID;
}

export const trackingInitialState: TrackingState = {
  driversWebData: webDataNotAsked(),
  pathGeojsonWebData: webDataNotAsked(),
  roadSpeedEventsWebData: webDataNotAsked(),
  vehicleSpeedEventsWebData: webDataNotAsked(),
  stopWebData: webDataNotAsked(),
  summaryWebData: webDataNotAsked(),
  incidentsWebData: webDataNotAsked(),
  vehicleWebData: webDataNotAsked(),
  deviceWebData: webDataNotAsked(),
  selectedTripTime: null,
  savedRoutesWebData: webDataNotAsked(),
  mediaEvents: webDataNotAsked(),
  selectedDate: undefined,
  selectedVehicleId: undefined,
  selectedSavedRoute: undefined,
  selectedTrigger: undefined,
  selectedGeofenceId: undefined,
  trackingTabOpen: true,
  devicesVehiclesWebData: webDataNotAsked(),
  selectedDeviceId: undefined,
};

export default (state: TrackingState = trackingInitialState, action: TrackingAction): TrackingState => {
  switch (action.type) {
    case ActionTypes.SET_TRACKING_PARAMETERS:
      return {
        ...state,
        selectedDate: action.payload.startTimestamp,
        selectedTripTime: null,
        selectedVehicleId: action.payload.vehicleId,
      };

    case ActionTypes.LOAD_TRACKING_REQUEST:
      return {
        ...state,
        driversWebData: webDataFetching(),
        pathGeojsonWebData: webDataFetching(),
        roadSpeedEventsWebData: webDataFetching(),
        vehicleSpeedEventsWebData: webDataFetching(),
        stopWebData: webDataFetching(),
        summaryWebData: webDataFetching(),
        incidentsWebData: webDataFetching(),
        vehicleWebData: webDataFetching(),
        deviceWebData: webDataFetching(),
        selectedDate: action.payload.startTimestamp,
        selectedTripTime: null,
        selectedVehicleId: action.payload.vehicleId,
      };

    case ActionTypes.LOAD_TRACKING_SUCCEEDED:
      return {
        ...state,
        driversWebData: webDataSuccess(action.payload.drivers),
        pathGeojsonWebData: webDataSuccess(action.payload.pathGeojson),
        roadSpeedEventsWebData: webDataSuccess(action.payload.roadSpeedEvents),
        vehicleSpeedEventsWebData: webDataSuccess(action.payload.vehicleSpeedEvents),
        stopWebData: webDataSuccess(action.payload.stops),
        summaryWebData: webDataSuccess(action.payload.summary),
        incidentsWebData: webDataSuccess(action.payload.incidents),
        vehicleWebData: webDataSuccess(action.payload.vehicle),
        deviceWebData: webDataSuccess(action.payload.device),
      };

    case ActionTypes.LOAD_TRACKING_FAILED:
      return {
        ...state,
        driversWebData: webDataError(),
        pathGeojsonWebData: webDataError(),
        roadSpeedEventsWebData: webDataError(),
        vehicleSpeedEventsWebData: webDataError(),
        stopWebData: webDataError(),
        summaryWebData: webDataError(),
        incidentsWebData: webDataError(),
        vehicleWebData: webDataError(),
        deviceWebData: webDataError(),
      };

    case ActionTypes.UPDATE_TRIP_FILTER_INTERVAL:
      return { ...state, selectedTripTime: action.payload };

    case ActionTypes.LOAD_SAVED_ROUTES_REQUEST:
      return {
        ...state,
        savedRoutesWebData: webDataFetching(),
        selectedSavedRoute: undefined,
      };

    case ActionTypes.LOAD_SAVED_ROUTES_SUCCEEDED:
      return {
        ...state,
        savedRoutesWebData: webDataSuccess(action.payload),
      };

    case ActionTypes.LOAD_SAVED_ROUTES_FAILED:
      return { ...state, savedRoutesWebData: webDataError() };

    case ActionTypes.CHANGED_PLANNED_ROUTE:
      return { ...state, selectedSavedRoute: action.payload };

    case ActionTypes.LOAD_MEDIA_EVENTS_SUCCEEDED:
      return { ...state, mediaEvents: webDataSuccess(action.payload) };

    case ActionTypes.LOAD_MEDIA_EVENTS_FAILED:
      return { ...state, mediaEvents: webDataError() };

    case ActionTypes.LOAD_MEDIA_EVENTS_REQUEST:
      return { ...state, mediaEvents: webDataFetching() };

    case ActionTypes.SHOW_TRACKING_TAB:
      return { ...state, trackingTabOpen: true };

    case ActionTypes.HIDE_TRACKING_TAB:
      return { ...state, trackingTabOpen: false };

    case ActionTypes.SET_SELECTED_DEVICE_ID:
      return { ...state, selectedDeviceId: action.payload };

    case ActionTypes.SET_SELECTED_TRIGGER:
      return { ...state, selectedTrigger: action.payload };

    case ActionTypes.SET_SELECTED_GEOFENCE_ID:
      return { ...state, selectedGeofenceId: action.payload };

    default:
      return state;
  }
};
